import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Box,
  Pagination,
  Alert,
  CircularProgress,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Collapse,
} from '@mui/material';
import {
  Edit,
  Delete,
  Visibility,
  GridView,
  FolderOpen,
  Folder,
  ExpandMore,
  Telegram,
} from '@mui/icons-material';
import { Image, apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ImageEditDialog from './ImageEditDialog';
import DetailedImageView from './DetailedImageView';
import PrimaryTagFilter from './PrimaryTagFilter';

import LazyImage, { placeholderImage } from './LazyImage';


interface ImageGalleryProps {
  onStatsUpdate?: () => void;
  uploadCounter?: number;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ onStatsUpdate, uploadCounter }) => {
  const { isAuthenticated, loading: authLoading, isGuest, hasPermission } = useAuth();
  const [images, setImages] = useState<Image[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editImage, setEditImage] = useState<Image | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const [detailImage, setDetailImage] = useState<Image | null>(null);

  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedPrimaryTagId, setSelectedPrimaryTagId] = useState<number | undefined>(undefined);
  const [fullyInsertableFilter, setFullyInsertableFilter] = useState<boolean | undefined>(undefined);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) =>
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const [viewMode, setViewMode] = useState<'grid' | 'collection'>('collection');

  useEffect(() => {
    // Only fetch images if authentication is complete and user is authenticated
    if (!authLoading && isAuthenticated) {
      fetchImages();
    } else if (!authLoading && !isAuthenticated) {
      setError('Authentication required');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedPrimaryTagId, fullyInsertableFilter, viewMode, authLoading, isAuthenticated, uploadCounter]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching images...', { isAuthenticated, authLoading });

      // Use larger page size for collection view to ensure all collections are visible
      const perPage = viewMode === 'collection' ? 100 : 12;
      const response = await apiService.getImages(page, perPage, undefined, selectedPrimaryTagId, fullyInsertableFilter);
      setImages(response.images || []);
      setTotalPages(response.pagination?.pages || 1);
    } catch (err: any) {
      console.error('Error fetching images:', err);
      if (err.message.includes('401')) {
        setError('Authentication required. Please log in.');
      } else {
        setError('Failed to load images. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handlePrimaryTagFilter = (primaryTagId?: number) => {
    setSelectedPrimaryTagId(primaryTagId);
    setPage(1); // Reset to first page when filtering
  };

  const handleFullyInsertableFilter = (fullyInsertable?: boolean) => {
    setFullyInsertableFilter(fullyInsertable);
    setPage(1); // Reset to first page when filtering
  };

  const handleEditImage = (image: Image) => {
    setEditImage(image);
    setEditDialogOpen(true);
  };

  const handleViewDetails = (image: Image) => {
    setDetailImage(image);
    setDetailDialogOpen(true);
  };

  const handleImageUpdate = (updatedImage: Image) => {
    setImages(prevImages =>
      prevImages.map(img => img.id === updatedImage.id ? updatedImage : img)
    );
  };

  const handleUpdateImage = async (imageId: number, data: any) => {
    try {
      await apiService.updateImage(imageId, data);
      await fetchImages();
      if (onStatsUpdate) onStatsUpdate();
    } catch (error) {
      console.error('Error updating image:', error);
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await apiService.deleteImage(imageId);
        await fetchImages();
        if (onStatsUpdate) onStatsUpdate();
      } catch (error) {
        console.error('Error deleting image:', error);
      }
    }
  };

  const handleTelegramShare = async (image: Image) => {
    try {
      console.log('🔵 Starting Telegram share from gallery...', image.title);

      // Prepare image data for Telegram sharing
      const imageData = {
        id: image.id,
        filename: image.filename,
        title: image.title,
        description: image.description,
        category: image.category,
        tags: image.tags.map(tag => tag.name),
        primaryTag: image.primary_tag?.name || null,
        fileSize: image.file_size,
        dimensions: image.width && image.height ? `${image.width}x${image.height}` : null,
        // Physical properties
        length: image.length,
        maxThickness: image.max_thickness,
        fullyInsertable: image.fully_insertable,
        softness: image.softness,
        // URLs for bot access
        imageUrl: getImageUrl(image.filename),
        webUrl: window.location.href,
        createdAt: image.created_at
      };

      const token = apiService.getAccessToken() || localStorage.getItem('admin_token') || '';

      let response: Response | undefined;
      let result: any = {};

      if (token) {
        console.log('🔵 Trying authenticated endpoint...');
        response = await fetch('https://pucollection.com/api/telegram/share', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(imageData)
        });

        result = await response.json();
        console.log('🔵 Authenticated response:', response.status, result);
      }

      if (response?.ok && result.success) {
        alert('✅ Image successfully shared to Telegram!');
        console.log('✅ Telegram share successful:', result);
      } else {
        const errorMsg = result.error || 'Failed to share to Telegram';
        alert(`❌ Error sharing to Telegram: ${errorMsg}`);
        console.error('❌ Telegram share failed:', result);
      }

    } catch (error) {
      console.error('❌ Failed to share to Telegram:', error);
      alert(`❌ Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const getImageUrl = (_filename: string) => placeholderImage;



  const ImageComponent = LazyImage;

  const readOnly = isGuest && !hasPermission('write');

  const viewerActions = (image: Image) => (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: '100%' }}>
      {(hasPermission('read') || hasPermission('details')) && (
        <Button
          fullWidth
          variant="outlined"
          color="info"
          startIcon={<Visibility />}
          onClick={() => handleViewDetails(image)}
          aria-label={`View details of ${image.title}`}
          sx={{ minHeight: 44, whiteSpace: 'nowrap' }}
        >
          Details
        </Button>
      )}
      {hasPermission('telegram') && (
        <Button
          fullWidth
          variant="outlined"
          color="info"
          startIcon={<Telegram />}
          onClick={() => handleTelegramShare(image)}
          aria-label={`Share ${image.title} to Telegram`}
          sx={{ minHeight: 44, whiteSpace: 'nowrap' }}
        >
          Telegram
        </Button>
      )}
    </Box>
  );

  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newViewMode: 'grid' | 'collection' | null) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  // Group images by primary tag for collection view - MEMOIZED to prevent recalculation
  const imageCollections = React.useMemo(() => {
    const collections = new Map();

    // Group images by primary tag
    images.forEach(image => {
      const primaryTag = image.primary_tag;
      const key = primaryTag ? `primary_${primaryTag.id}` : 'untagged';
      const name = primaryTag ? primaryTag.name : 'Untagged';
      const color = primaryTag ? primaryTag.color : '#666666';

      if (!collections.has(key)) {
        collections.set(key, {
          key,
          name,
          color,
          images: [],
          isPrimaryTag: true,
        });
      }
      collections.get(key).images.push(image);
    });

    // Sort images within each collection by max_thickness/diameter (smallest to largest)
    collections.forEach(collection => {
      collection.images.sort((a: Image, b: Image) => {
        // Primary sort: by max_thickness (diameter)
        const thicknessA = a.max_thickness || 0;
        const thicknessB = b.max_thickness || 0;
        if (thicknessA !== thicknessB) {
          return thicknessA - thicknessB;
        }

        // Secondary sort: by length if max_thickness is equal
        const lengthA = a.length || 0;
        const lengthB = b.length || 0;
        if (lengthA !== lengthB) {
          return lengthA - lengthB;
        }

        // Tertiary sort: by title if both thickness and length are equal
        return a.title.localeCompare(b.title);
      });
    });

    return Array.from(collections.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [images]); // Only recalculate when images array changes

  // Show loading spinner while authentication is being checked
  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Checking authentication...</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  if (images.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" gutterBottom>No Images Found</Typography>
        <Typography variant="body1" color="text.secondary">
          Upload some images to get started!
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: { xs: 2, sm: 3 } }}>
        <Typography component="h2" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
          Image Gallery ({images.length} images)
        </Typography>

        {/* View Mode Toggle */}
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          aria-label="view mode"
          size="small"
        >
          <ToggleButton value="grid" aria-label="grid view">
            <GridView />
          </ToggleButton>
          <ToggleButton value="collection" aria-label="collection view">
            <FolderOpen />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Filters */}
      <>
        {/* Primary Tag Filter - only show in grid mode */}
        {viewMode === 'grid' && (
          <PrimaryTagFilter
            selectedPrimaryTagId={selectedPrimaryTagId}
            onPrimaryTagSelect={handlePrimaryTagFilter}
          />
        )}

        {/* Fully Insertable Filter - show in both views */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: { xs: 1, sm: 2 }, mb: 2 }}>
          <ToggleButtonGroup
            aria-label="Filter by insertable"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
            value={fullyInsertableFilter === undefined ? null : fullyInsertableFilter.toString()}
            exclusive
            onChange={(event, value) => {
              handleFullyInsertableFilter(value === null ? undefined : value === 'true');
            }}
            size="small"
          >
            <ToggleButton value="true" sx={{ flex: { xs: 1, sm: 'none' } }}>Fully insertable</ToggleButton>
            <ToggleButton value="false" sx={{ flex: { xs: 1, sm: 'none' } }}>Not fully insertable</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </>

      {/* Grid View */}
      {viewMode === 'grid' && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(auto-fill, minmax(min(100%, 150px), 1fr))', sm: 'repeat(auto-fill, minmax(300px, 1fr))' },
          gap: { xs: 1.5, sm: 3 }
        }}>
          {images.map((image) => (
            <Card key={image.id} elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  height: { xs: 150, sm: 200 },
                  backgroundColor: 'grey.800',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  '@media (hover: hover) and (prefers-reduced-motion: no-preference)': {
                    '&:hover': {
                      opacity: 0.9,
                      transform: 'scale(1.02)',
                      transition: 'all 0.2s ease-in-out'
                    }
                  }
                }}
              >
                <ImageComponent
                  filename={image.filename}
                  alt={image.title}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                  onClick={() => handleViewDetails(image)}
                />
              </Box>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h6" component="div">
                  {image.title}
                </Typography>
                {image.description && (
                  <Typography variant="body2" color="text.secondary">
                    {image.description.length > 100
                      ? `${image.description.substring(0, 100)}...`
                      : image.description}
                  </Typography>
                )}

                {/* Tag */}
                {image.primary_tag && (
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={image.primary_tag.name}
                      size="small"
                      sx={{ backgroundColor: image.primary_tag.color, color: 'white', fontSize: '0.7rem', height: 20 }}
                    />
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {(image.file_size / (1024 * 1024)).toFixed(2)} MB •
                  {new Date(image.created_at).toLocaleDateString()}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between' }}>
                {readOnly ? viewerActions(image) : (<Box>
                  {/* View Details - requires 'read' or 'details' permission */}
                  {hasPermission('read') || hasPermission('details') ? (
                    <IconButton onClick={() => handleViewDetails(image)} color="info" aria-label={`View details of ${image.title}`}>
                      <Visibility />
                    </IconButton>
                  ) : null}

                  {/* Telegram Share - requires 'telegram' permission */}
                  {hasPermission('telegram') && (
                    <IconButton onClick={() => handleTelegramShare(image)} color="info" aria-label={`Share ${image.title} to Telegram`}>
                      <Telegram />
                    </IconButton>
                  )}

                  {/* Edit/Delete - only for non-guest users or 'write' permission */}
                  {(!isGuest || hasPermission('write')) && (
                    <>
                      <IconButton onClick={() => handleEditImage(image)} color="primary" aria-label={`Edit ${image.title}`}>
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteImage(image.id)} color="error" aria-label={`Delete ${image.title}`}>
                        <Delete />
                      </IconButton>
                    </>
                  )}
                </Box>)}
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Collection View */}
      {viewMode === 'collection' && (
        <Box>
          {imageCollections.map((collection) => (
            <Card key={collection.key} elevation={1} sx={{ mb: 2 }}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                {/* Collection Header */}
                <Box component="h3" sx={{ m: 0 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => toggleGroup(String(collection.key))}
                    aria-expanded={!collapsedGroups.has(String(collection.key))}
                    sx={{
                      width: '100%',
                      font: 'inherit',
                      border: 0,
                      cursor: 'pointer',
                      textAlign: 'left',
                      backgroundColor: `${collection.color}20`,
                      borderLeft: `6px solid ${collection.color}`,
                      color: 'text.primary',
                      minHeight: 56,
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    {collapsedGroups.has(String(collection.key)) ? <Folder aria-hidden /> : <FolderOpen aria-hidden />}
                    <Typography variant="h6" component="span" sx={{ fontWeight: 'bold', flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      {collection.name}
                    </Typography>
                    <Chip
                      label={collection.images.length}
                      size="small"
                      aria-label={`${collection.images.length} items`}
                      sx={{ backgroundColor: collection.color, color: 'white', fontWeight: 'bold' }}
                    />
                    <ExpandMore
                      aria-hidden
                      sx={{ transform: collapsedGroups.has(String(collection.key)) ? 'rotate(-90deg)' : 'none', transition: 'transform 0.2s' }}
                    />
                  </Box>
                </Box>

                {/* Collection Content */}
                <Collapse in={!collapsedGroups.has(String(collection.key))} unmountOnExit>
                <Box sx={{ p: { xs: 1, sm: 2 } }}>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(auto-fill, minmax(min(100%, 150px), 1fr))', sm: 'repeat(auto-fill, minmax(250px, 1fr))' },
                    gap: { xs: 1, sm: 2 },
                    pt: 1
                  }}>
                    {collection.images.map((image: Image) => (
                      <Card key={image.id} elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box
                          sx={{
                            height: 150,
                            backgroundColor: 'grey.800',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.9,
                              transform: 'scale(1.02)',
                              transition: 'all 0.2s ease-in-out'
                            }
                          }}
                        >
                          <ImageComponent
                            filename={image.filename}
                            alt={image.title}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain'
                            }}
                            onClick={() => handleViewDetails(image)}
                          />
                        </Box>
                        <CardContent sx={{ flexGrow: 1, p: 1.5 }}>
                          <Typography gutterBottom variant="subtitle2" component="div">
                            {image.title}
                          </Typography>
                          {image.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                              {image.description.length > 60
                                ? `${image.description.substring(0, 60)}...`
                                : image.description}
                            </Typography>
                          )}

                          {/* The group is already shown by the section header */}

                          {!readOnly && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              {(image.file_size / (1024 * 1024)).toFixed(1)} MB
                            </Typography>
                          )}
                        </CardContent>
                        <CardActions sx={{ justifyContent: 'center', p: 1 }}>
                          {readOnly && viewerActions(image)}
                          {/* View Details - requires 'read' or 'details' permission */}
                          {!readOnly && (hasPermission('read') || hasPermission('details')) && (
                            <IconButton size="small" onClick={() => handleViewDetails(image)} color="info" aria-label={`View details of ${image.title}`}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          )}

                          {/* Telegram Share - requires 'telegram' permission */}
                          {!readOnly && hasPermission('telegram') && (
                            <IconButton size="small" onClick={() => handleTelegramShare(image)} color="info" aria-label={`Share ${image.title} to Telegram`}>
                              <Telegram fontSize="small" />
                            </IconButton>
                          )}

                          {/* Edit/Delete - only for non-guest users or 'write' permission */}
                          {(!isGuest || hasPermission('write')) && (
                            <>
                              <IconButton size="small" onClick={() => handleEditImage(image)} color="primary" aria-label={`Edit ${image.title}`}>
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => handleDeleteImage(image.id)} color="error" aria-label={`Delete ${image.title}`}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </CardActions>
                      </Card>
                    ))}
                  </Box>
                </Box>
                </Collapse>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {viewMode === 'grid' && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      <ImageEditDialog
        open={editDialogOpen}
        image={editImage}
        onClose={() => {
          setEditDialogOpen(false);
          setEditImage(null);
        }}
        onSave={handleUpdateImage}
      />

      <DetailedImageView
        image={detailImage}
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setDetailImage(null);
        }}
        onEdit={handleEditImage}
        onDelete={handleDeleteImage}
        getImageUrl={getImageUrl}
        isGuest={isGuest}
        hasPermission={hasPermission}
      />
    </Box>
  );
};

export default ImageGallery;
